import { useState } from "react";
import { ArrowUpRight, MapPin, Clock, CheckCircle2, Mail, Phone, Instagram } from "lucide-react";
import { toast } from "sonner";
import { MaskedLines, FadeIn, Overline } from "@/components/motion/Reveal";
import { Seo } from "@/components/Seo";
import { submitLead } from "@/lib/api";
import { FIRM_LOCATION, CONTACT, INTEREST_OPTIONS } from "@/constants/content";

const initialForm = { name: "", email: "", phone: "", interest: "General Consultation", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitLead(form);
      setSent(true);
      setForm(initialForm);
      toast.success("Message received. We will be in touch shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-48 pb-32">
      <Seo
        title="Contact Us — Clear Horizon Consulting"
        description="Reach Clear Horizon Consulting in Khalda, Amman — send an inquiry, email info@clearhorizon-consulting.com, call +962 79 757 4143, or message us on WhatsApp."
        path="/contact"
      />
      <h1 className="sr-only">Contact Clear Horizon Consulting</h1>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-6">
          <FadeIn y={16}>
            <Overline>Contact</Overline>
          </FadeIn>
          <MaskedLines
            className="mt-8 font-serif font-light tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl"
            delay={0.15}
            lines={["Get in", <span key="l2" className="italic text-primary">touch.</span>]}
          />
          <FadeIn delay={0.5} className="mt-10 max-w-md">
            <p className="text-base font-light text-muted-foreground leading-relaxed">
              Tell us where your business stands and where it needs to go.
              A senior consultant reviews every inquiry personally.
            </p>
          </FadeIn>

          <FadeIn delay={0.65} className="mt-16 space-y-8">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Office</p>
                <p className="mt-2 text-sm text-foreground">{FIRM_LOCATION}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Mail className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Email</p>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="mt-2 block text-sm text-foreground hover:text-primary transition-colors duration-300">{CONTACT.email}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Phone</p>
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="mt-2 block text-sm text-foreground hover:text-primary transition-colors duration-300">{CONTACT.phone}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Instagram className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Instagram</p>
                <a href={CONTACT.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="mt-2 block text-sm text-foreground hover:text-primary transition-colors duration-300">{CONTACT.instagramHandle}</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Hours</p>
                <p className="mt-2 text-sm text-foreground">Sunday – Thursday, 10:00 AM – 4:00 PM</p>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10">
              <p className="text-sm text-muted-foreground">Prefer to chat first?</p>
              <a href="https://wa.me/962797574143" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="mt-2 inline-flex items-center gap-2 text-primary text-sm hover:gap-4 transition-[gap] duration-300">
                Message us on WhatsApp <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </FadeIn>
        </div>

        <FadeIn delay={0.3} className="lg:col-span-5 lg:col-start-8">
          {sent ? (
            <div data-testid="contact-success" className="h-full min-h-[420px] flex flex-col items-start justify-center border border-white/10 p-10">
              <CheckCircle2 className="w-10 h-10 text-primary" />
              <h2 className="mt-8 font-serif text-3xl font-light">Message received.</h2>
              <p className="mt-4 text-sm font-light text-muted-foreground leading-relaxed">
                Thank you for reaching out. A member of our team will respond within one business day.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSent(false)}
                className="mt-10 text-sm text-primary hover:underline underline-offset-4"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form data-testid="contact-form" onSubmit={handleSubmit} className="space-y-10 lg:pt-24">
              <div>
                <label htmlFor="contact-name" className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Name</label>
                <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your full name" className="input-line mt-2" />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Email</label>
                <input id="contact-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className="input-line mt-2" />
              </div>
              <div>
                <label htmlFor="contact-phone" className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Phone</label>
                <input id="contact-phone" data-testid="contact-phone-input" type="tel" required minLength={5} value={form.phone} onChange={set("phone")} placeholder="+962 ..." className="input-line mt-2" />
              </div>
              <div>
                <label htmlFor="contact-interest" className="text-xs tracking-[0.2em] uppercase text-muted-foreground">I'm interested in</label>
                <select
                  id="contact-interest"
                  data-testid="contact-interest-select"
                  value={form.interest}
                  onChange={set("interest")}
                  className="input-line mt-2 bg-background"
                >
                  {INTEREST_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-background text-foreground">{o}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="contact-message" className="text-xs tracking-[0.2em] uppercase text-muted-foreground">Message</label>
                <textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder="Tell us about your business and what you need..." className="input-line mt-2 resize-none" />
              </div>
              <button
                type="submit"
                data-testid="contact-submit-button"
                disabled={sending}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-medium px-10 py-4 hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:hover:scale-100"
              >
                {sending ? "Sending..." : "Send Message"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          )}
        </FadeIn>
      </div>
    </main>
  );
}
